import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useQueryClient } from "@tanstack/react-query";

import { ApiError, apiPost } from "../api/client";
import { extractTimeoutConfirmTrackingId, mapRehydrationError } from "../api/errors";

const DEBOUNCE_MS = 500;

// Extrai o primeiro UUID válido (qualquer versão) de um texto arbitrário.
// Aceita inputs como `UUID("79337fa8-...")`, `id=79337fa8-...`, logs, CSVs.
const UUID_EXTRACT_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function extractFirstUuid(input: string): string | null {
  const match = input.match(UUID_EXTRACT_REGEX);
  return match ? match[0] : null;
}

// Schema estrito: campo DEVE ser um UUID puro. Inputs sujos (`UUID("...")`,
// `id=...`, log com UUID embutido) são normalizados pelo onPaste antes do
// validator rodar. Se o usuário digitar qualquer coisa extra depois, o erro
// aparece imediatamente em vez de silenciar e submeter um input dirty.
const singleTicketSchema = z.object({
  ticketId: z
    .string()
    .trim()
    .min(1, "Informe o TicketID")
    .uuid("TicketID inválido (esperado UUID)"),
});

type SingleTicketFormValues = z.infer<typeof singleTicketSchema>;

type RehydrateResponse = { trackingId: string };

type ToastState =
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

type SingleTicketFormProps = {
  onSubmitSuccess?: () => void;
};

export function SingleTicketForm({ onSubmitSuccess }: SingleTicketFormProps) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmitAtRef = useRef<number>(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SingleTicketFormValues>({
    resolver: zodResolver(singleTicketSchema),
    defaultValues: { ticketId: "" },
    mode: "onSubmit",
  });

  // Cola algo arbitrário (`UUID("...")`, `id=...`, log, etc.) e o campo
  // já fica com o UUID limpo extraído. Sem isso o usuário cola e fica
  // confuso achando que precisa editar manualmente o texto.
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;
    const extracted = extractFirstUuid(pasted);
    if (extracted) {
      event.preventDefault();
      setValue("ticketId", extracted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    // Sem UUID válido — deixa o paste seguir e o validator do submit
    // mostra "Nenhum UUID encontrado no texto".
  };

  const onSubmit = handleSubmit(async (values) => {
    const now = Date.now();
    if (isSubmitting) {
      return;
    }
    if (now - lastSubmitAtRef.current < DEBOUNCE_MS) {
      return;
    }
    lastSubmitAtRef.current = now;

    // Schema estrito garante que values.ticketId já é um UUID válido.
    const ticketId = values.ticketId.trim();
    setIsSubmitting(true);
    try {
      const response = await apiPost<RehydrateResponse>(
        "/bff/api/v1/rehydrate",
        { ticketId },
      );
      if (!isMountedRef.current) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["operations"] });
      onSubmitSuccess?.();

      reset({ ticketId: "" });
      setToast({
        kind: "success",
        message: `Reidratação iniciada (trackingId: ${response.trackingId})`,
      });
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }
      const recoveredTrackingId = extractTimeoutConfirmTrackingId(err);
      if (recoveredTrackingId) {
        // BFF não confirmou a tempo, mas a op pode ter começado no
        // downstream. Invalida a lista mesmo assim — quando o server
        // responder com o registro, vai aparecer na home.
        await queryClient.invalidateQueries({ queryKey: ["operations"] });
        onSubmitSuccess?.();
        reset({ ticketId: "" });
        setToast({
          kind: "success",
          message: `Reidratação enviada — aguardando confirmação (trackingId: ${recoveredTrackingId})`,
        });
      } else {
        // Erro terminal (409, 400, etc) — limpa o campo pra não ficar com
        // o UUID problemático + invalida a lista pra mostrar o registro
        // que o service JÁ criou no Mongo (StartSingle + Fail), mantendo
        // paridade com o comportamento do batch (batch sempre mostra).
        if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
          reset({ ticketId: "" });
          await queryClient.invalidateQueries({ queryKey: ["operations"] });
        }
        setToast({ kind: "error", message: mapRehydrationError(err) });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  });

  const handleCloseToast = () => setToast(null);

  return (
    <Box component="section" aria-label="Reidratação de um ticket">
      <Stack spacing={2} component="form" noValidate onSubmit={onSubmit}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Reidratar um ticket
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cole o TicketID (UUID).
        </Typography>

        <TextField
          label="TicketID"
          fullWidth
          autoFocus
          error={Boolean(errors.ticketId)}
          helperText={errors.ticketId?.message ?? " "}
          onPaste={handlePaste}
          inputProps={{
            "aria-label": "TicketID",
            spellCheck: false,
            autoCapitalize: "off",
            autoCorrect: "off",
          }}
          {...register("ticketId")}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            disabled={isSubmitting}
            sx={{ fontWeight: 600, textTransform: "none", minWidth: 160 }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {isSubmitting ? "Enviando..." : "Reidratar"}
          </Button>
        </Box>
      </Stack>

      {toast ? (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toast.kind === "success" ? "success" : "error"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ) : null}
    </Box>
  );
}
